// src/Components/ImportMarkers/AssignPropertyContent.js

import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import dayjs from 'dayjs';

const InvertedCaseTextField = ({
  value,
  onChange,
  placeholder,
  disabled,
  makeUppercase,
  autoFocus,
  id,
}) => {
  const inputRef = useRef(null);
  const [localValue, setLocalValue] = useState(value || '');

  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  const insertAtCursor = (textToInsert) => {
    const input = inputRef.current;
    if (!input) return;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const val = input.value;
    const newVal = val.slice(0, start) + textToInsert + val.slice(end);

    setLocalValue(newVal);
    onChange(newVal);

    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.selectionStart = inputRef.current.selectionEnd =
          start + textToInsert.length;
      }
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (makeUppercase && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      const keyLower = e.key.toLowerCase();
      const keyUpper = e.key.toUpperCase();

      if (keyLower !== keyUpper) {
        e.preventDefault();
        e.stopPropagation();
        const char = e.shiftKey ? keyLower : keyUpper;
        insertAtCursor(char);
      }
    }
  };

  const handlePaste = (e) => {
    if (makeUppercase) {
      e.preventDefault();
      e.stopPropagation();
      const paste = (e.clipboardData || window.clipboardData)
        .getData('text')
        .toUpperCase();
      insertAtCursor(paste);
    }
  };

  const handleChange = (e) => {
    setLocalValue(e.target.value);
    onChange(e.target.value);
  };

  return (
    <TextField
      inputRef={inputRef}
      inputProps={{ id }}
      value={localValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      placeholder={placeholder}
      fullWidth
      size='small'
      disabled={disabled}
      autoFocus={autoFocus}
      sx={{
        mt: 0.5,
        backgroundColor: disabled ? '#f5f5f5' : '#fff',
        '& .MuiInputBase-root': { height: '26px' },
        '& .MuiInputBase-input': {
          py: '0px',
          px: '8px',
          textTransform: 'none !important',
        },
      }}
    />
  );
};

const TARGET_OPTIONS = [
  'ICON',
  'POSITION (LAT, LNG)',
  'VISIBILITY',
  'HOVER PHOTO',
  'HOVER TEXT',
  'INFO',
  'PHOTOS',
  'DOCUMENTS',
  'DESCRIPTION',
];

const INFO_OPTIONS = [
  'DATE',
  'LOCATION ID',
  'TITLE',
  'WEB SITE',
  'LOCATION ADDRESS',
  'CITY',
  'STATE',
  'POSTCODE/ZIP',
  'COUNTRY',
  'CLIENT NAME',
  'CATEGORY',
  'REGION',
  'CONTROLLER',
  'KEYWORDS',
  'SHOWCASE',
  'HILIGHT',
];

const VISIBILITY_OPTIONS = ['PUBLIC', 'PUBLIC FROM', 'RESTRICTED', 'PRIVATE'];

const getIconUrl = (iconObj) => {
  if (!iconObj) return null;
  return (
    iconObj.url ||
    iconObj.imageUrl ||
    iconObj.iconUrl ||
    iconObj.downloadURL ||
    iconObj.image ||
    iconObj.src ||
    iconObj.fileUrl ||
    (typeof iconObj.icon === 'string' ? iconObj.icon : null)
  );
};

export default function AssignPropertyContent({
  state,
  makeUppercase,
  assignedTo,
  setAssignedTo,
  selection,
  setSelection,
  valueSource,
  setValueSource,
  staticValue,
  setStaticValue,
  pendingMappingValue,
  setPendingMappingValue,
  sortedIcons,
  isDatePickerOpen,
  isDatePickerOpenRef,
  openPicker,
  closePicker,
  onSubmit,
}) {
  const isUnspecified = state.invalidMatchState?.type === 'TEXT';

  const rawCellValue = state.invalidMatchState?.cellValue;
  const isNA =
    typeof rawCellValue === 'string' && rawCellValue.trim().toUpperCase() === 'N/A';
  const displayCellValue = isNA ? '' : rawCellValue;

  // If property is unmapped in the template, show the cell value as read-only
  if (isUnspecified) {
    const cleanTextValue =
      displayCellValue !== undefined && displayCellValue !== null
        ? String(displayCellValue)
            .replace(/<\/p>\s*<p[^>]*>/gi, ' ')
            .replace(/<[^>]*>?/gm, '')
            .trim()
        : '';

    const formattedReadOnlyValue =
      makeUppercase && typeof cleanTextValue === 'string'
        ? cleanTextValue.toUpperCase()
        : cleanTextValue;

    return (
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}>
        <Box>
          <Typography
            variant='caption'
            sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
            Value
          </Typography>
          <TextField
            fullWidth
            size='small'
            value={formattedReadOnlyValue}
            disabled
            sx={{
              mt: 0.5,
              backgroundColor: '#f5f5f5',
              '& .MuiInputBase-root': { height: '26px' },
              '& .MuiInputBase-input': {
                py: '0px',
                px: '8px',
                color: '#333',
                WebkitTextFillColor: '#333',
                fontStyle: !formattedReadOnlyValue ? 'italic' : 'normal',
              },
            }}
          />
        </Box>
      </Box>
    );
  }

  const isTemplateMismatch = state.invalidMatchState?.type === 'TEMPLATE_MISMATCH';
  const showThreeTierLayout = isTemplateMismatch; // Updated to match only Template Mismatch

  const isBooleanField =
    assignedTo === 'INFO' && (selection === 'SHOWCASE' || selection === 'HILIGHT');
  const isDateField = assignedTo === 'INFO' && selection === 'DATE';

  const renderMuiDatePicker = () => {
    const cleanDateStr = String(staticValue || '')
      .replace(/^PUBLIC FROM\s+/i, '')
      .trim();
    const activeDate = cleanDateStr ? dayjs(cleanDateStr) : null;
    const isValid = activeDate ? activeDate.isValid() : false;

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DesktopDatePicker
          format='YYYY-MM-DD'
          value={isValid ? activeDate : null}
          open={isDatePickerOpen}
          onOpen={(e) => openPicker(e)}
          onClose={() => closePicker()}
          onChange={(newValue) => {
            setValueSource('custom');
            setStaticValue(
              newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : '',
            );
          }}
          onAccept={() => {
            closePicker();
            setTimeout(() => onSubmit(), 150);
          }}
          slotProps={{
            popper: {
              sx: { zIndex: 100050 },
            },
            textField: {
              id: 'custom-val-input',
              size: 'small',
              fullWidth: true,
              onClick: (e) => openPicker(e),
              onKeyDown: (e) => {
                if (e.key === ' ' || e.code === 'Space' || e.key === 'Enter') {
                  if (!isDatePickerOpenRef.current) {
                    openPicker(e);
                  }
                }
              },
              sx: {
                mt: 0.5,
                backgroundColor: '#fff',
                '& .MuiInputBase-root': { height: '26px' },
                '& .MuiInputBase-input': { py: '0px', px: '8px' },
              },
            },
          }}
        />
      </LocalizationProvider>
    );
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}>
      {showThreeTierLayout ? (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: '1 1 45%' }}>
            <Typography
              variant='caption'
              sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
              Assigned To
            </Typography>
            <Select
              fullWidth
              size='small'
              SelectDisplayProps={{ id: 'assigned-to-select' }}
              value={TARGET_OPTIONS.includes(assignedTo) ? assignedTo : ''}
              onChange={(e) => {
                const val = e.target.value;
                setAssignedTo(val);
                setSelection(val === 'VISIBILITY' ? 'PUBLIC' : '');
              }}
              sx={{
                mt: 0.5,
                backgroundColor: '#fff',
                height: '26px',
                '& .MuiSelect-select': {
                  py: '0px',
                  minHeight: '26px',
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
              MenuProps={{ style: { zIndex: 100020 } }}>
              <MenuItem value=''>
                <em style={{ color: '#999' }}>Select mapping...</em>
              </MenuItem>
              {TARGET_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </Box>

          {assignedTo === 'VISIBILITY' ? (
            <Box sx={{ flex: '1 1 100%' }}>
              <Typography
                variant='caption'
                sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                Visibility State
              </Typography>
              <RadioGroup
                row
                name='visibility-radios'
                value={selection}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelection(val);
                  if (val === 'PUBLIC FROM') {
                    let extractedDate = '';
                    if (
                      rawCellValue &&
                      String(rawCellValue).toUpperCase().startsWith('PUBLIC FROM')
                    ) {
                      extractedDate = String(rawCellValue)
                        .replace(/^PUBLIC FROM\s+/i, '')
                        .trim();
                    }

                    let currentStaticDate = String(staticValue || '')
                      .replace(/^PUBLIC FROM\s+/i, '')
                      .trim();

                    if (!currentStaticDate) {
                      if (extractedDate) {
                        setStaticValue(extractedDate);
                        setValueSource('custom');
                      } else if (state.defaultPublicFromDate) {
                        setStaticValue(state.defaultPublicFromDate);
                        setValueSource('custom');
                      }
                    } else {
                      setStaticValue(currentStaticDate);
                      setValueSource('custom');
                    }
                  } else {
                    setStaticValue('');
                  }
                }}
                sx={{ ml: 1 }}>
                {VISIBILITY_OPTIONS.map((opt) => (
                  <FormControlLabel
                    key={opt}
                    value={opt}
                    control={<Radio color='primary' size='small' sx={{ p: 0.5 }} />}
                    label={<Typography variant='body2'>{opt}</Typography>}
                  />
                ))}
              </RadioGroup>
              {selection === 'PUBLIC FROM' && renderMuiDatePicker()}
            </Box>
          ) : (
            <Box sx={{ flex: '1 1 45%' }}>
              <Typography
                variant='caption'
                sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                Selection
              </Typography>
              <Select
                fullWidth
                size='small'
                SelectDisplayProps={{ id: 'selection-select' }}
                value={
                  assignedTo === 'INFO' && INFO_OPTIONS.includes(selection)
                    ? selection
                    : ''
                }
                disabled={assignedTo !== 'INFO'}
                onChange={(e) => setSelection(e.target.value)}
                sx={{
                  mt: 0.5,
                  backgroundColor: '#fff',
                  height: '26px',
                  '& .MuiSelect-select': {
                    py: '0px',
                    minHeight: '26px',
                    display: 'flex',
                    alignItems: 'center',
                  },
                }}
                MenuProps={{ style: { zIndex: 100020 } }}>
                <MenuItem value=''>
                  <em style={{ color: '#999' }}>Select Selection field...</em>
                </MenuItem>
                {assignedTo === 'INFO' &&
                  INFO_OPTIONS.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
              </Select>
            </Box>
          )}

          {isTemplateMismatch && assignedTo !== 'VISIBILITY' && (
            <Box sx={{ flex: '1 1 100%' }}>
              <Typography
                variant='caption'
                sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                Value
              </Typography>
              {isBooleanField ? (
                <FormControlLabel
                  control={
                    <Checkbox
                      inputProps={{ id: 'custom-val-input' }}
                      checked={
                        String(staticValue).trim().toLowerCase() === 'true' ||
                        String(staticValue).trim().toLowerCase() === 'yes' ||
                        String(staticValue).trim() === '1'
                      }
                      onChange={(e) => {
                        setValueSource('custom');
                        setStaticValue(e.target.checked ? 'true' : 'false');
                      }}
                      size='small'
                      sx={{ p: 0.5 }}
                    />
                  }
                  label={
                    <Typography variant='body2'>
                      {selection === 'SHOWCASE' ? 'Enable Showcase' : 'Enable Hilight'}
                    </Typography>
                  }
                  sx={{ mt: 0.5, ml: 0 }}
                />
              ) : isDateField ? (
                renderMuiDatePicker()
              ) : (
                <InvertedCaseTextField
                  key={`static-val-${state.invalidMatchState?.colName}`}
                  id='custom-val-input'
                  value={staticValue}
                  onChange={(val) => {
                    setValueSource('custom');
                    setStaticValue(val);
                  }}
                  placeholder='Enter value...'
                  makeUppercase={makeUppercase}
                  autoFocus={false}
                />
              )}
            </Box>
          )}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {state.invalidMatchState?.type !== 'ICON' && (
            <Box>
              <Typography
                variant='caption'
                sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                Original Excel Value
              </Typography>
              <TextField
                fullWidth
                size='small'
                value={
                  makeUppercase && typeof displayCellValue === 'string'
                    ? displayCellValue.toUpperCase()
                    : displayCellValue || ''
                }
                disabled
                sx={{
                  mt: 0.5,
                  backgroundColor: '#f5f5f5',
                  '& .MuiInputBase-root': { height: '26px' },
                  '& .MuiInputBase-input': {
                    py: '0px',
                    px: '8px',
                    color: '#333',
                    WebkitTextFillColor: '#333',
                    fontStyle: !displayCellValue ? 'italic' : 'normal',
                  },
                }}
              />
            </Box>
          )}

          <Box>
            <Typography
              variant='caption'
              sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
              Map to Existing{' '}
              {state.invalidMatchState?.type === 'REGION'
                ? 'Region'
                : state.invalidMatchState?.type === 'CATEGORY'
                  ? 'Category'
                  : 'Icon'}
            </Typography>

            {state.invalidMatchState?.type === 'ICON' ? (
              <Box
                id='icon-tray-container'
                tabIndex={0}
                sx={{
                  mt: 0.5,
                  p: 1,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  backgroundColor: '#fff',
                  border: '1px solid #ccc',
                  borderRadius: 1,
                  maxHeight: '220px',
                  overflowY: 'auto',
                  '&:focus': { outline: '2px solid #1976d2' },
                }}>
                {sortedIcons.map((icon) => {
                  const iconImgUrl = getIconUrl(icon);
                  const isSelected = pendingMappingValue === icon.id;

                  return (
                    <Box
                      key={icon.id}
                      id={`icon-item-${icon.id}`}
                      tabIndex={-1}
                      onClick={() => setPendingMappingValue(icon.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.stopPropagation();
                          setPendingMappingValue(icon.id);
                          setTimeout(() => onSubmit(), 150);
                        }
                      }}
                      sx={{
                        width: 64,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        cursor: 'pointer',
                        border: isSelected
                          ? '1px solid #1976d2'
                          : '1px solid transparent',
                        backgroundColor: isSelected
                          ? 'rgba(25, 118, 210, 0.08)'
                          : 'transparent',
                        borderRadius: '4px',
                        p: 0.5,
                        '&:hover': {
                          border: isSelected ? '1px solid #1976d2' : '1px solid #bbb',
                        },
                        '&:focus': {
                          outline: 'none',
                          border: '1px solid #1976d2',
                          backgroundColor: 'rgba(25, 118, 210, 0.15)',
                        },
                      }}>
                      {iconImgUrl ? (
                        <img
                          src={iconImgUrl}
                          alt={icon.name || 'icon'}
                          style={{
                            width: 28,
                            height: 28,
                            objectFit: 'contain',
                            marginBottom: '6px',
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            backgroundColor: '#00B0FF',
                            marginBottom: '6px',
                          }}
                        />
                      )}
                      <Typography
                        sx={{
                          fontSize: '10px',
                          textAlign: 'center',
                          lineHeight: 1.1,
                          minHeight: '1.1em',
                          display: 'flex',
                          alignItems: 'center',
                          wordBreak: 'break-word',
                          color: isSelected ? '#1976d2' : 'text.primary',
                        }}>
                        {icon.name || '\u00A0'}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Select
                fullWidth
                size='small'
                displayEmpty
                SelectDisplayProps={{ id: 'mapping-select-trigger' }}
                value={
                  state.invalidMatchState?.type === 'REGION' &&
                  state.regions?.some((r) => String(r.id) === String(pendingMappingValue))
                    ? String(pendingMappingValue)
                    : state.invalidMatchState?.type === 'CATEGORY' &&
                        state.categories?.some(
                          (c) => String(c.id) === String(pendingMappingValue),
                        )
                      ? String(pendingMappingValue)
                      : ''
                }
                onChange={(e) => setPendingMappingValue(e.target.value)}
                sx={{
                  mt: 0.5,
                  backgroundColor: '#fff',
                  height: '26px',
                  '& .MuiSelect-select': {
                    py: '0px',
                    minHeight: '26px',
                    display: 'flex',
                    alignItems: 'center',
                  },
                }}
                MenuProps={{ style: { zIndex: 100020 } }}>
                <MenuItem value=''>
                  <em style={{ color: '#999' }}>Select...</em>
                </MenuItem>
                {state.invalidMatchState?.type === 'REGION' &&
                  state.regions?.map((r) => (
                    <MenuItem key={r.id} value={String(r.id)}>
                      {r.name}
                    </MenuItem>
                  ))}
                {state.invalidMatchState?.type === 'CATEGORY' &&
                  state.categories?.map((c) => (
                    <MenuItem key={c.id} value={String(c.id)}>
                      {c.name}
                    </MenuItem>
                  ))}
              </Select>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}
